<template>
  <div
    class="absolute left-0 top-0 h-full w-full bg-transparent"
    :class="openItemBox ? 'block' : 'hidden'"
    @click="closeItemBox"
  />
  <div class="relative z-10">
    <input
      :id="id"
      v-model="input"
      class="w-full border border-transparent p-4 text-sm hover:border-placeholder-gray focus:bg-white focus:outline-none"
      :class="
        openItemBox
          ? 'rounded-t-md border-l border-r border-t border-body-color bg-white'
          : 'rounded-md bg-off-white'
      "
      :placeholder="placeholder"
      @click="showOptions"
      @input="handleInput"
    />
    <img
      v-if="showSearchIcon"
      class="absolute right-4 top-4"
      src="@/assets/search.svg"
      alt="plus icon"
    />
    <div
      v-if="openItemBox"
      class="item-container nowebscroll absolute max-h-40 w-full overflow-y-auto rounded-b-md border-b border-l border-r border-body-color bg-white"
    >
      <div
        v-if="isLoading"
        class="py-2"
      >
        <img
          class="-top-2px loading relative h-8 w-full"
          src="@/assets/loading-v2-yellow.svg"
          alt=""
        />
      </div>
      <div v-if="showItemBox">
        <div
          v-for="(item, i) of filteredItems"
          :key="i"
          class="flex cursor-pointer justify-between rounded-md px-4 py-2 text-gray-over-dark hover:bg-off-white"
          @click="onSelect(item)"
        >
          <p>{{ item[firstField] }}</p>
          <p class="text-right">
            {{ item[secondField] }}
          </p>
        </div>
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';

export default {
  name: 'ComboboxInput',
  props: {
    items: { type: Array, required: true },
    placeholder: String,
    handleSelect: Function,
    showSearchIcon: {
      type: Boolean,
      default: true,
    },
    showItemBoxOnOpen: {
      type: Boolean,
      default: false,
    },
    value: String,
    closeBox: {
      type: Boolean,
      default: false,
    },
    id: String,
    searchInput: Function,
    firstField: {
      type: String,
      default: 'name',
    },
    secondField: {
      type: String,
      default: 'location',
    },
    selectedField: String,
    customFormatItems: Function,
  },
  data() {
    return {
      input: '',
      displayItems: false,
      openItemBox: false,
      filteredItems: [],
      isLoading: false,
    };
  },
  computed: {
    showItemBox: function () {
      if (this.showItemBoxOnOpen && this.openItemBox) {
        return true;
      } else if (this.openItemBox && this.displayItems) {
        return true;
      } else {
        return false;
      }
    },
  },
  watch: {
    items() {
      this.filteredItems = this.items;
    },
    closeBox(val) {
      if (val) {
        this.closeItemBox();
      }
    },
    input: function () {
      this.triggerDebouncedSearch();
    },
  },
  mounted() {
    this.filteredItems = this.items;
    if (this.value) {
      this.input = this.value;
    }
  },
  methods: {
    showOptions() {
      this.openItemBox = true;
    },
    formatItems(items) {
      return items.map((item) => {
        const findDot =
          item?.identifiers?.length > 0
            ? item.identifiers.find((el) => el?.is_type === 'DOT')
            : null;
        return { ...item, dot_number: findDot?.value };
      });
    },
    updatedResults(items) {
      const formattedItems = this.customFormatItems
        ? this.customFormatItems(items)
        : this.formatItems(items);
      this.filteredItems = formattedItems;
      this.isLoading = false;
    },
    triggerDebouncedSearch: debounce(function () {
      if (this.searchInput && this.input.length > 0) {
        this.isLoading = true;
        this.searchInput(this.input, this.updatedResults);
      }
    }, 500),
    handleInput() {
      if (this.input.length > 0) {
        this.openItemBox = true;
        if (this.searchInput) {
          // handle this better -- until actual api calls are setup, we need the else to run
        } else {
          this.displayItems = true;
          const filtered = this.items.filter(
            (el) =>
              el.name?.toLowerCase().includes(this.input?.toLowerCase()) ||
              el.location?.toLowerCase().includes(this.input?.toLowerCase())
          );
          this.filteredItems = filtered;
        }
      } else {
        this.filteredItems = this.items;
        this.displayItems = false;
      }
    },
    onSelect(item) {
      this.handleSelect(item);
      this.input = item[this.selectedField || this.secondField];
      this.openItemBox = false;
    },
    closeItemBox() {
      this.openItemBox = false;
    },
  },
};
</script>
<style scoped>
.item-container {
  min-height: 1rem;
}
.nowebscroll::-webkit-scrollbar {
  display: none;
}

.nowebscroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>

<!-- 
  Exampe implementation

  <combobox-input
    placeholder="Enter company name"
    :items="[
      {name: 'Factoring Comp', location: 'St. Louis, MO'},
      {name: 'Factoring Company', location: 'Dallas, TX'},
      {name: 'Factoring Companies', location: 'Anchorage, AK'}
      ]"
    :handleSelect="handleSelect"
  >
    <div
      class="flex items-center pb-2 pt-4"
      @click="openModal"
    >
      <p class="text-body-color mr-8">Add New Company</p>
      <img
        src="@/assets/plus.svg"
        alt="plus icon"
      />
    </div>
  </combobox-input>
  -->
